import type {AJAXResponseProductData} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {ANALYTICS_ACTION_FOR_DOWNLOAD, ProductType, SelectionType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import type {
  AJAXResponseOrderGenerationStatus,
  AJAXResponseOrderSuccess,
  AJAXResponseOutOfFreeDownloads,
  AJAXResponsePurchaseProduct,
  OrderDetailsForRedux,
  VideoFreebieParams,
} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-panel.types';
import {GA4EventParamName} from '@Libraries/ga-events';
import {
  setErrorMessageForPublishing,
  setErrorOnPublishPanel,
  setFreebieProduct,
  setOrderDetails,
  setOrderError,
  setPremiumProductForPoster,
  setPremiumProductForPrint,
  setShowLoader,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';

import {SubFlowType} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-compiled-sub-panel/digital-signage-compiled-sub-panel.types';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';

const STATUS_DOWNLOADABLE = 'downloadable';
const STATUS_NEEDS_ATTENTION = 'needs-attention';
const CONVERT_TRIAL_MODE_OUT_OF_FREE_DOWNLOADS = 'out-of-free-downloads';
const MAX_ELAPSED_TIME_TO_SHOW_ERROR = 600;

let productUsed = {} as AJAXResponseProductData;
let downloadElapsedTime = 0;
let posterName: string | undefined = '';
let showTrialDialog: boolean = false;
const stripeCheckoutCloseCallback: any = null;
const onCheckoutDownloadSuccessCallback: any = null;
let showError = false;
let AJAXCallMade = false;

export function isNumericNew(value: string): boolean {
  const parsedValue = parseFloat(value);
  return !isNaN(parsedValue) && isFinite(parsedValue);
}

function noop(): void {}

export function getMode(): string {
  const {subFlowType, selectionType} = window.PMW.redux.store.getState().publishingOptionsPanel;
  let mode: string;
  if (subFlowType !== SubFlowType.DEFAULT) {
    switch (subFlowType) {
      case SubFlowType.RAYDIANT_SUBDISPLAY:
        mode = 'raydiant';
        break;
      case SubFlowType.SIGNAGELIVE_WIDGET_SUBDISPLAY:
        mode = 'signagelive';
        break;
      case SubFlowType.SCREEN_CLOUD_SUBDISPLAY:
        mode = 'screencloud';
        break;
      default:
        mode = 'download';
        break;
    }
  } else if (selectionType !== SelectionType.DEFAULT_DISPLAY) {
    switch (selectionType) {
      case SelectionType.DIGITAL_SIGNAGE_DISPLAY:
        mode = 'digital-signage';
        break;
      case SelectionType.EMBED_WEBPAGE_DISPLAY:
        mode = 'embed-links';
        break;
      default:
        mode = 'download';
        break;
    }
  } else {
    mode = 'download';
  }
  return mode;
}

// codeReviewSarim: divide this into smaller, readable functions please
export function updateDownloadProgress(realPercentage: number = 0, isHeavyOrder: boolean = false, estimatedTimeStr: string = '', estimatedTimeInSeconds: number = 0): number {
  // Get Previous Percentage from Redux
  // Set Email Option in Redux
  // See about Extra Info Too
  // const sendEmailOption = false;

  let sleepTime = 3;
  const previousPercentage = window.PMW.redux.store.getState().publishingOptionsPanel.orderDetails.progressPercentage;
  let newPercentage = previousPercentage;

  // codeReviewSarim: constants...
  const incrementStep = 2; // Step by which percentage is increased
  const incrementInterval = 400; // Time interval in milliseconds

  // codeReviewSarim: saalve it
  if (realPercentage === 100) {
    newPercentage = realPercentage;
    window.PMW.redux.store.dispatch(
      setOrderDetails({
        progressPercentage: newPercentage,
        estimatedTimeInSeconds: estimatedTimeStr,
        timeElapsedInSeconds: downloadElapsedTime,
        error: '',
      })
    );
    return 0;
  }

  if (realPercentage < 100 || newPercentage < 100) {
    if (realPercentage > previousPercentage) {
      const incrementIntervalId = setInterval(() => {
        if (newPercentage < realPercentage) {
          newPercentage += incrementStep;
          if (newPercentage > realPercentage) {
            newPercentage = realPercentage; // Ensure it doesn't exceed the real percentage
          }
          window.PMW.redux.store.dispatch(
            setOrderDetails({
              progressPercentage: newPercentage,
              estimatedTimeInSeconds: estimatedTimeStr,
              timeElapsedInSeconds: downloadElapsedTime,
              error: '',
            })
          );
        } else {
          clearInterval(incrementIntervalId); // Stop incrementing once target is reached
        }
      }, incrementInterval);
    } else if (previousPercentage <= 90) {
      newPercentage = previousPercentage + 5;

      // Update the redux store with the new percentage
      window.PMW.redux.store.dispatch(
        setOrderDetails({
          progressPercentage: newPercentage,
          estimatedTimeInSeconds: estimatedTimeStr,
          timeElapsedInSeconds: downloadElapsedTime,
          error: '',
        })
      );
    }
  }

  const percentageDiff = newPercentage - realPercentage;

  if (percentageDiff > 20 && percentageDiff < 40) {
    sleepTime = 5;
  } else if (percentageDiff >= 40) {
    sleepTime = 10;
  } else {
    sleepTime = 3;
  }

  if (isHeavyOrder) {
    sleepTime *= 2;
  }

  return sleepTime;
}

export function updateOrderEmailNotification(orderId: string, value: boolean): void {
  void window.PMW.writeLocal('Ordergenerator/updateOrderNotificationForSendingEmail', {
    idorder: orderId,
    value: value ? 1 : 0,
  }).catch(() => {
    showError = true;
  });
}

export function onGetStatusSuccess(idOrder: string, response: AJAXResponseOrderGenerationStatus): void {
  if (response) {
    const {percentageDone, estimatedTimeInSeconds, estimatedTimeStr, idProduct, heaviness, error} = response;
    const isHeavyOrder: boolean = parseInt(heaviness, 10) > 0;
    const {productType} = window.PMW.redux.store.getState().publishingOptionsPanel;

    if (error === STATUS_NEEDS_ATTENTION) {
      window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_order_generation_error')));
      window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
      updateOrderEmailNotification(idOrder, true);
      return;
    }

    const data: OrderDetailsForRedux = {
      progressPercentage: percentageDone,
      estimatedTimeInSeconds: estimatedTimeStr,
      timeElapsedInSeconds: downloadElapsedTime,
      error: '',
    };

    const updatedOrderId = {
      ...productUsed,
      orderId: idOrder,
    };

    if (productType === ProductType.POSTER) {
      window.PMW.redux.store.dispatch(setPremiumProductForPoster(updatedOrderId));
    } else if (productType === ProductType.PRINT) {
      window.PMW.redux.store.dispatch(setPremiumProductForPrint(updatedOrderId));
    }

    // codeReviewSarim: why do we need progressPercentage from store?
    const {progressPercentage} = window.PMW.redux.store.getState().publishingOptionsPanel.orderDetails;

    if (percentageDone < 100 || progressPercentage < 100) {
      const sleepTime = updateDownloadProgress(percentageDone, isHeavyOrder, estimatedTimeStr, estimatedTimeInSeconds);
      downloadElapsedTime += sleepTime;
      if (downloadElapsedTime > MAX_ELAPSED_TIME_TO_SHOW_ERROR) {
        updateOrderEmailNotification(idOrder, true);
        const text = `ERROR: Unable to generate the order:${idOrder} in ${downloadElapsedTime}seconds.`;
        window.PMW.redux.store.dispatch(setOrderError(text));
      } else if (window.PMW.redux.store.getState().publishingOptionsPanel.showLoader) {
        if (Number(percentageDone) < 100) {
          getOrderGenerationStatus(idOrder, percentageDone, sleepTime);
        } else {
          setTimeout(() => {
            onGetStatusSuccess(idOrder, response);
          }, 3000);
        }
      }
    } else {
      data.progressPercentage = 100;
      window.PMW.redux.store.dispatch(setOrderDetails(data));

      const updatedProductUsed = {
        ...productUsed,
        orderId: idOrder,
        status: STATUS_DOWNLOADABLE,
        needsPurchase: false,
        needsRegenerationDueToDeletion: false,
      };

      if (productType === ProductType.POSTER) {
        window.PMW.redux.store.dispatch(setPremiumProductForPoster(updatedProductUsed));
      } else if (productType === ProductType.PRINT) {
        window.PMW.redux.store.dispatch(setPremiumProductForPrint(updatedProductUsed));
      }
      window.PMW.redux.store.dispatch(setShowLoader(false));
    }
  } else {
    window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
  }
}

export function getOrderGenerationStatus(idOrder: string, percentageDone: number, sleepTime: number): void {
  void window.PMW.readLocal('cart/getRealTimeOrderProgress', {
    id: idOrder,
    percentageDone,
    sleepTime,
  })
    .then((response: AJAXResponseOrderGenerationStatus) => {
      onGetStatusSuccess(idOrder, response);
    })
    .catch((e) => {
      window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
    });
}

export function showDownloadProgress(data: AJAXResponseOrderSuccess): void {
  const {idOrder} = data;
  if (idOrder && ((isNumericNew(idOrder) && parseInt(idOrder, 10) > 0) || !isNumericNew(idOrder))) {
    // codeReviewSarim: constants, what is 3 sleep time supposed to mean
    getOrderGenerationStatus(idOrder, 0, 3);
  } else {
    window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
  }
}

export function onGenerateOrderSuccess(response: AJAXResponseOrderSuccess): void {
  try {
    if (response) {
      window.PMW.gtm.trackGA4PremiumDownloadEvents(response);
      const isRealTime = response.isRealTime ? 1 : 0;
      if (isRealTime) {
        showDownloadProgress(response);
      } else {
        window.location.href = window.PMW.util.site_url(`cart/preparingDownload/${response.type}/${response.idOrder}/${isRealTime}`);
      }
    }
  } catch (e) {
    window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
  }
}

// codeReviewSarim: productId is a string, why?
export function newPremiumDownload(productId: string, posterId: string): void {
  void window.PMW.writeLocal('cart/newPremiumDownload/', {
    id: Number(productId),
    hid: posterId,
  })
    .then((response) => {
      onGenerateOrderSuccess(response);
    })
    .catch((e) => {
      switch (e.code) {
        case 'permission_denied':
          // codeReviewSarim: this seems like a regular use case where on error state you are showing a message and setting state to true
          // so you should make a new function and dispatch(setErrorMessage(errMsg)) and it also sets your state to true, or something like that
          window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_download_designs_you_own')));
          window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
          return;
        case 'getty_requirements_not_met':
          window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_cannot_buy_only_getty_images')));
          window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
          return;
        case 'order_not_found':
        case 'purchase_not_found':
        case 'param_missing':
        case 'queue_error':
        default:
          window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_something_wrong_reload_page')));
          window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
      }
    });
}

export function onFreeDownloadFromReactBuilder(): void {
  if (productUsed.isVideo) {
    void (window.posterEditor && window.posterEditor.whiteboard
      ? window.posterEditor.whiteboard.savePoster.save().then(() => {
          posterName = window?.posterEditor?.whiteboard?.name;
          downloadVideoFreebie();
        })
      : noop());
  } else {
    void (window.posterEditor && window.posterEditor.whiteboard
      ? window.posterEditor.whiteboard.savePoster.save().then(() => {
          posterName = window?.posterEditor?.whiteboard?.name;
          downloadImagefreebie();
        })
      : noop());
  }
}

function downloadImagefreebie(): void {
  // Call updateDownloadProgress every 1 seconds to show fake image generation progress
  updateDownloadProgress(20);

  const progress = setInterval(() => {
    updateDownloadProgress(50);

    const {progressPercentage} = window.PMW.redux.store.getState().publishingOptionsPanel.orderDetails;

    if (progressPercentage >= 95) {
      window.PMW.redux.store.dispatch(setShowLoader(false));
      clearInterval(progress);
      AJAXCallMade = false;
      return;
    }

    if (!AJAXCallMade) {
      AJAXCallMade = true;
      void window.PMW.writeLocal('poster/getImageFreebie', {
        hid: productUsed.hashedId,
      })
        .then((response) => {
          const updatedProductUsed = {
            ...productUsed,
            status: STATUS_DOWNLOADABLE,
          };

          window.PMW.redux.store.dispatch(setFreebieProduct(updatedProductUsed));
          const data: OrderDetailsForRedux = {
            progressPercentage: 100,
            estimatedTimeInSeconds: '',
            timeElapsedInSeconds: 0,
            error: '',
          };
          window.PMW.redux.store.dispatch(setOrderDetails(data));
        })
        .catch(() => {
          window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
        });
    }
  }, 100);
}

function downloadVideoFreebie(): void {
  updateDownloadProgress(10);
  const posterHashId = productUsed.hashedId;
  const progress = setInterval(() => {
    updateDownloadProgress(5);
  }, 2000);

  void window.PMW.pollingAjaxCallAsync(generateVideoFreebie.bind(null, posterHashId), getVideoFreebieStatus.bind(null, posterHashId))
    .then((response: VideoFreebieParams) => {
      if (response) {
        const updatedProductUsed = {
          ...productUsed,
          status: STATUS_DOWNLOADABLE,
        };
        window.PMW.redux.store.dispatch(setFreebieProduct(updatedProductUsed));
        const data: OrderDetailsForRedux = {
          progressPercentage: 100,
          estimatedTimeInSeconds: '',
          timeElapsedInSeconds: 0,
          error: '',
        };
        clearInterval(progress);
        window.PMW.redux.store.dispatch(setOrderDetails(data));
        window.PMW.redux.store.dispatch(setShowLoader(false));
      }
    })
    .catch(() => {
      window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
      showError = true;
    });
}

function generateVideoFreebie(posterHashId: string): Promise<any> {
  return window.PMW.readLocal('poster/generateVideoFreebieNew', {
    idPosterHash: posterHashId,
  });
}

function getVideoFreebieStatus(posterHashId: string): Promise<any> {
  return window.PMW.readLocal('poster/getVideoFreebieStatus', {
    posterHashId,
  });
}

export function purchaseDownload(productId: string, hashedId: string): void {
  const mode = getMode();
  void window.PMW.writeLocal('cart/checkoutpublish', {
    idposter: hashedId,
    idproduct: Number(productId),
    mode,
  })
    .then((response: AJAXResponsePurchaseProduct) => {
      oncheckoutDownloadSuccess(response);
    })
    .catch(() => {
      window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
      showError = true;
    });
}

function onCheckoutCloseCallback(): void {
  if (typeof stripeCheckoutCloseCallback === 'function') {
    stripeCheckoutCloseCallback();
  }
}

function oncheckoutDownloadSuccess(response: AJAXResponsePurchaseProduct): void {
  if (typeof onCheckoutDownloadSuccessCallback === 'function') {
    onCheckoutDownloadSuccessCallback();
  }

  if (response.url) {
    window.location.href = response.url;
  } else if (response.checkout) {
    if (typeof response.checkout.doUpsellPremium !== 'undefined') {
      showTrialDialog = response.checkout.doUpsellPremium;
    }

    if (window.PMW.enableCheckoutV2) {
      window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
        window.PMW.stripeCheckout.showStripeCheckoutForDownloadPurchase(
          response.checkout.priceId,
          Number(response.checkout.id),
          response.checkout.posterHash,
          Number(response.checkout.idProduct),
          response.checkout.is ?? false,
          showDownloadProgress,
          onCheckoutCloseCallback
        );
      });
    } else {
      response.checkout.cb = showDownloadProgress;
      response.checkout.onCloseCallBack = onCheckoutCloseCallback;
      window.PMW.checkout.showCheckoutDialog(window.PMW.checkout.MODE_DOWNLOAD, response.checkout);
    }
  } else if (response.premium > 0 && response.message.length > 0) {
    window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
    setErrorMessageForPublishing(window.i18next.t('pmwjs_stock_cannot_be_downloaded'));
  }
}

function regenerateOrder(idOrder: string | null): void {
  if (window.PMW.getUserPremiumLevel() > USER_PREMIUM_LEVELS.PAYG) {
    void window.PMW.writeLocal('cart/premiumRegen/', {
      id: idOrder,
    })
      .then(onGenerateOrderSuccess)
      .catch(() => {
        window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
      });
  } else {
    void window.PMW.writeLocal(`cart/nonPremiumRegen/${idOrder}`, {})
      .then(onGenerateOrderSuccess)
      .catch(() => {
        window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
      });
  }
}

function openCheckoutDialogForTrialConversion(trialConversionMode: string, productId: string, creditPrice: number): void {
  if (trialConversionMode === 'out-of-free-downloads') {
    window.PMW.getUserPremiumLevel();
  }
  if (window.PMW.enableCheckoutV2) {
    window.PMW.premiumDialogs.openConvertTrialDialog(
      trialConversionMode,
      onConvertTrial.bind(null, trialConversionMode, productId, creditPrice),
      onConvertTrialFailCallback.bind(trialConversionMode),
      onCheckoutCloseCallback
    );
  } else {
    window.PMW.checkout.showCheckoutDialog(window.PMW.checkout.MODE_CONVERT_TRIAL, {
      cb(data: AJAXResponseOutOfFreeDownloads): void {
        if (typeof onCheckoutDownloadSuccessCallback === 'function') {
          onCheckoutDownloadSuccessCallback();
        }
        onConvertTrial(trialConversionMode, productId, creditPrice, data);
      },
      onCheckoutFailCallback: onConvertTrialFailCallback.bind(null, trialConversionMode),
      trialConversionMode,
    });
  }
}

function onConvertTrial(mode: string, productId: string, creditPrice: number, data: AJAXResponseOutOfFreeDownloads): void {
  window.PMW.trackOnlineAdsEvent(
    window.PMW.gtm.ONLINE_ADS_EVENT_TYPE_SUBSCRIPTION_PURCHASE,
    window.PMW.gtm.PURCHASE_ORDER_TYPE_SUBSCRIPTION,
    data.orderValue,
    data.idSubscriptionPurchase
  );
  trackGA4PurchaseOnConvertTrial(data);
  newPremiumDownload(productId, productUsed.hashedId);
}

function onConvertTrialFailCallback(trialConversionReason: string): void {
  if (trialConversionReason === CONVERT_TRIAL_MODE_OUT_OF_FREE_DOWNLOADS) {
    void window.PMW.writeLocal('premium/cancelTrialSubscriptionOutOfFreeDownloads')
      .then((response) => {
        // let message = r.status === 'success' ? window.i18next.t('pmwjs_unable_charge_card_switched_free_plan') : i18next.t('pmwjs_unable_charge_card_try_again');
        // showConvertTrialPaymentError(message);
      })
      .catch(() => {
        window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
      });
    // .fail(showConvertTrialPaymentError.bind(this, i18next.t('pmwjs_error_message')));
  }
  // else {
  //   showConvertTrialPaymentError(i18next.t('pmwjs_unable_charge_card_try_again'));
  // }
}

function trackGA4PurchaseOnConvertTrial(data: AJAXResponseOutOfFreeDownloads): void {
  if (data && data.ga4PurchaseEventParams) {
    window.PMW.gtm.trackGA4PurchaseEvent(data.ga4PurchaseEventParams);
  }
}

function callBackForCheckoutThroughReactBuilder(response: any) {
  if (response.isPremiumUser) {
    let status = '';
    let idProduct = 0;
    if (typeof response.status === 'string' && response.status.length > 0) {
      status = response.status;
    }
    if (typeof response.premiumPurchase !== 'undefined') {
      idProduct = response.premiumPurchase.idProduct;
      if (idProduct > 0) {
        if (status === 'new-order') {
          window.posterEditor && window.posterEditor.whiteboard
            ? window.posterEditor.whiteboard.savePoster.save().then(() => {
                newPremiumDownload(String(idProduct), productUsed.posterId);
              })
            : $.noop();
        } else if (status === 'out-of-free-downloads') {
          window.PMW.gtm.trackGA4CustomEvent('TrialUserOutOfDownloads');
          openCheckoutDialogForTrialConversion(status, String(idProduct), parseInt(response.premiumPurchase.creditPrice));
        } else {
          window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
          window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_checkout_download_error')));
        }
      } else {
        window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
        window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_checkout_download_error')));
      }
    } else {
      window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
      window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_checkout_download_error')));
    }
  } else if (response.checkout) {
    if (typeof response.checkout.doUpsellPremium !== 'undefined') {
      // Set the flag to show trial dialog on download purchase.
      setShowTrialDialogFlag_(response.checkout.doUpsellPremium);
    }

    if (window.PMW.enableCheckoutV2) {
      window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
        window.PMW.stripeCheckout.showStripeCheckoutForDownloadPurchase(
          response.checkout.priceId,
          response.checkout.id,
          response.checkout.posterHash,
          response.checkout.idProduct,
          response.checkout.is,
          showDownloadProgress,
          onCheckoutCloseCallback
        );
      });
    } else {
      response.checkout.cb = showDownloadProgress;
      response.checkout.onCloseCallBack = onCheckoutCloseCallback;
      window.PMW.checkout.showCheckoutDialog(window.PMW.checkout.MODE_DOWNLOAD, response.checkout, null);
    }
  } else {
    window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
    window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_checkout_download_error')));
  }
}

function setShowTrialDialogFlag_(val: any) {
  showTrialDialog = val;
}

// codeReviewSarim: firstly this function shouldn't be called processDesignNew, secondly it doesn't belong in publishing-embed-in-webpage-library? it is more general no?

// codeReviewSarim: mode should be an enum, and opts needs to be some interface too, see what is sent as opts

// codeReviewSarim: instead of AjaxResponseProductData, it should be PosterProductData or something
export function processDesignNew(mode: string, selectedProduct: AJAXResponseProductData, opts: object | null): void {
  if (typeof opts === 'undefined') {
    // opts = {};
  }

  // codeReviewSarim: remove commented code
  // if (opts.hasOwnProperty('onCheckoutDownloadSuccessCallback')) {
  //   onCheckoutDownloadSuccessCallback_ = opts.onCheckoutDownloadSuccessCallback;
  //   delete opts.onCheckoutDownloadSuccessCallback;
  // }
  //
  // if (opts.hasOwnProperty('stripeCheckoutCloseCallback')) {
  //   stripeCheckoutCloseCallback_ = opts.stripeCheckoutCloseCallback;
  //   delete opts.stripeCheckoutCloseCallback;
  // }
  //
  // const productInfo: ProductInfo = {
  //   status: '',
  //   productId: '',
  //   downloadUrl: '',
  //   orderId: null,
  //   productCreditPrice: 0,
  //   downloadFileUrl: 0,
  // };
  // Adding this if else case for backwards compatibility as right now Download dialog has been partially moved to React
  // if (selectedProduct) {
  // converted this to string so change it back to the variable
  // if (mode === 'DATA_VIEW_DOWNLOAD') {
  //   // Case when PrcessDesign is called from download panel
  //   // productInfo.status = selectedProduct.status;
  //   // productInfo.productId = selectedProduct.id;
  //   // productInfo.downloadUrl = selectedProduct.downloadUrl;
  //   // productInfo.orderId = selectedProduct.orderId;
  //   // productInfo.productCreditPrice = selectedProduct.productCreditPrice;
  //   // productInfo.downloadFileUrl = selectedProduct.downloadFileUrl;
  //   // if (Object.keys(opts).length > 0) {
  //   //   downloadOptions_ = opts;
  //   // }
  // } else {
  //   productInfo.status = selectedProduct.status;
  //   productInfo.productId = selectedProduct.id;
  //   productInfo.downloadUrl = selectedProduct.downloadUrl;
  //   productInfo.orderId = selectedProduct.orderId;
  //   productInfo.productCreditPrice = selectedProduct.productCreditPrice;
  //   productInfo.downloadFileUrl = selectedProduct.downloadFileUrl;
  // }
  // }

  // codeReviewSarim: remove logs
  productUsed = selectedProduct;
  const {status, downloadUrl, downloadFileUrl, productCreditPrice, orderId} = productUsed;
  const productId = productUsed.id;
  const idPosterHash = window.PMW.redux.store.getState().publishingOptionsPanel.hashedId;
  posterName = productUsed.name;

  // codeReviewSarim: all these ifs should be their own functions
  // handleDownloadableOrderState or something, come up with good names and call those
  // codeReviewSarim: none of these strings below should be magic strings, they need to be properly typed in enums or put in constants
  if (status === 'downloadable') {
    if (orderId) {
      void updateOrderLastDownloadedOn(orderId);
    }

    if (productUsed.needsRegenerationDueToDeletion) {
      if (orderId) {
        processOrderRegeneration(orderId);
      } else {
        showError = true;
      }
    } else {
      window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_DOWNLOAD, {[GA4EventParamName.TYPE]: 'redownload'});
      window.PMW.redux.store.dispatch(setShowLoader(false));
      return;
    }
  }
  if (status === 'outdated') {
    if (orderId) {
      processOrderRegeneration(orderId);
    } else {
      showError = true;
    }
  } else if (status === 'new-order') {
    if (productId.length > 0) {
      // codeReviewSarim: this can be cleaned up, window.posterEditor?.whiteboard?.savePoster?.save()?.then()....
      if (window.PMW.isReactPosterMaker()) {
        void (window.posterEditor && window.posterEditor.whiteboard
          ? window.posterEditor.whiteboard.savePoster.save().then(() => {
              posterName = window?.posterEditor?.whiteboard?.name;
              newPremiumDownload(productId, idPosterHash);
            })
          : noop());
      } else {
        newPremiumDownload(productId, idPosterHash);
      }
      window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_DOWNLOAD, {[GA4EventParamName.TYPE]: 'freePremium'});
    } else {
      showError = true;
    }
  } else if (status === 'out-of-free-downloads') {
    window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_DOWNLOAD, {[GA4EventParamName.TYPE]: 'TrialUserOutOfDownloads'});
    if (productId.length > 0) {
      openCheckoutDialogForTrialConversion(status, productId, productCreditPrice);
    } else {
      showError = true;
    }
  } else if (status === 'unpurchased') {
    if (productId.length > 0) {
      if (window.PMW.isReactPosterMaker()) {
        function checkoutDownload(p: any): Promise<any> {
          return window.PMW.writeLocal('cart/checkoutpublish', p);
        }

        function onCheckoutDownloadResponse(response: any) {
          if (response === null) {
            window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
            window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
          } else {
            if (response.orderRegen) {
              window.PMW.freeEditDialog(response);
              return;
            }
            if (response.message && response.premium > 0) {
              window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
              window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_cannot_purchase_premium_content')));
            } else if (response.url) {
              window.location.href = response.url;
            } else if (response.isPremiumUser || response.checkout) {
              if (typeof callBackForCheckoutThroughReactBuilder === 'function') {
                callBackForCheckoutThroughReactBuilder(response);
              }
            } else {
              window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
              window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
            }
          }
        }

        window.posterEditor && window.posterEditor.whiteboard
          ? window.posterEditor.whiteboard.savePoster.save().then(() => {
              const poster = window.posterEditor ? window.posterEditor.whiteboard : undefined;
              if (Number(productId) > 0 && poster) {
                checkoutDownload({
                  idposter: poster.hashedID,
                  idproduct: productId,
                  promotion: '',
                  mode: 'download',
                })
                  .then(onCheckoutDownloadResponse)
                  .catch(() => {
                    window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
                    window.PMW.redux.store.dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
                  });
              }
            })
          : $.noop();
      } else if (Number(window.PMW.getUserId()) > 0) {
        purchaseDownload(productId, idPosterHash);
      } else {
        window.location.href = window.PMW.util.site_url('authenticate/showlogin');
      }
      window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_DOWNLOAD, {[GA4EventParamName.TYPE]: 'currency'});
    } else {
      showError = true;
    }
  } else if (status === 'generating') {
    const object: AJAXResponseOrderSuccess = {
      type: '',
      isRealTime: false,
      idOrder: orderId,
    };
    showDownloadProgress(object);
  } else if (status === 'freebie') {
    if (window.PMW.isReactPosterMaker()) {
      onFreeDownloadFromReactBuilder();
    } else if (productUsed.isVideo) {
      downloadVideoFreebie();
    } else {
      downloadImagefreebie();
    }
    window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_DOWNLOAD, {[GA4EventParamName.TYPE]: 'free'});
  }
  // else if (status === 'download-video-jpeg') {
  //   // NOT USED in my case
  //   // downloadImageForVideo(selectedProduct.downloadFileUrl);
  // }
  else if (status === STATUS_NEEDS_ATTENTION) {
    window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
    window.PMW.redux.store.dispatch(
      setErrorMessageForPublishing(window.i18next.t('pmwjs_something_wrong_contact_support', {supportLink: window.PMW.util.site_url('user/feedbackform')}))
    );
  }
  if (showError) {
    showError = false;
    window.PMW.redux.store.dispatch(setErrorOnPublishPanel(true));
  }
}

const updateOrderLastDownloadedOn = async (orderId: string): Promise<void> => {
  await window.PMW.writeLocal('cart/updateOrderLastDownloadedOn', {
    orderId,
  });
};

const processOrderRegeneration = (orderId: string): void => {
  if (window.PMW.isReactPosterMaker()) {
    void (window.posterEditor && window.posterEditor?.whiteboard
      ? window.posterEditor.whiteboard.savePoster.save().then(() => {
          posterName = window.posterEditor?.whiteboard?.name;
          regenerateOrder(orderId);
        })
      : noop());
  } else {
    regenerateOrder(orderId);
  }
  window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_DOWNLOAD, {[GA4EventParamName.TYPE]: 'freeRegen'});
};
